import React from 'react';

// Define the structure for table data
export interface TableColumn {
  key: string;
  label: string;
  width?: string;
}

export interface TableCellConfig {
  value?: React.ReactNode;
  cellSettings?: {
    disabled?: boolean;
  };
}

export type TableCellValue = React.ReactNode | TableCellConfig;

export interface TableRow {
  id: string | number;
  [key: string]: TableCellValue;
}

const isTableCellConfig = (value: TableCellValue): value is TableCellConfig => {
  if (value === null || value === undefined) {
    return false;
  }

  if (React.isValidElement(value)) {
    return false;
  }

  if (typeof value !== 'object') {
    return false;
  }

  const candidate = value as TableCellConfig;
  return typeof candidate.value !== 'undefined' || typeof candidate.cellSettings !== 'undefined';
};

interface TableProps {
  columns: TableColumn[];
  data: TableRow[];
  showRowNumbers?: boolean;
  hover?: boolean;
  className?: string;
}

export const Table: React.FC<TableProps> = ({
  columns,
  data,
  showRowNumbers = false,
  hover = true,
  className = ''
}) => {
  return (
    <div className="overflow-x-auto">
      <table className={`table ${className}`}>
        {/* Table Header */}
        <thead>
          <tr>
            {showRowNumbers && <th></th>}
            {columns.map((column) => (
              <th key={column.key} style={{ width: column.width }} className={"whitespace-normal"}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        
        {/* Table Body */}
        <tbody>
          {data.map((row, index) => (
            <tr
              key={row.id}
              className={`${hover ? 'hover:bg-base-300' : ''}`}
            >
              {showRowNumbers && <th>{index + 1}</th>}
              {columns.map((column) => {
                const cell = row[column.key];

                if (isTableCellConfig(cell)) {
                  const { cellSettings, value } = cell;

                  if (cellSettings?.disabled) {
                    return (
                      <td
                        key={`${row.id}-${column.key}`}
                        style={{ backgroundColor: '#f0f0f0', color: '#a0a0a0' }}
                      />
                    );
                  }

                  return (
                    <td key={`${row.id}-${column.key}`}>
                      {value ?? null}
                    </td>
                  );
                }

                return (
                  <td key={`${row.id}-${column.key}`}>
                    {cell ?? null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Example usage component
export const ExampleTable: React.FC = () => {
  const columns: TableColumn[] = [
    { key: 'name', label: 'Name' },
    { key: 'job', label: 'Job' },
    { key: 'favoriteColor', label: 'Favorite Color' }
  ];

  const data: TableRow[] = [
    {
      id: 1,
      name: 'Cy Ganderton',
      job: 'Quality Control Specialist',
      favoriteColor: 'Blue'
    },
    {
      id: 2,
      name: 'Hart Hagerty',
      job: 'Desktop Support Technician',
      favoriteColor: 'Purple'
    },
    {
      id: 3,
      name: 'Brice Swyre',
      job: 'Tax Accountant',
      favoriteColor: 'Red'
    }
  ];

  return (
    <Table
      columns={columns}
      data={data}
      showRowNumbers={true}
      hover={true}
    />
  );
};
